import { useEffect, useState } from 'react';
import {
  AudienceCategoryItem,
  AudiencePlatformItem,
  AudienceSetItem,
  CountryItem,
  TaxonomyApi,
} from '@digiseg-labs/api-client';
import {
  getStoredCountryCode,
  getStoredPlatformCode,
  setStoredCountryCode,
  setStoredPlatformCode,
} from '../utils/prefs';

const isBrowser = typeof window !== 'undefined';

type UseTaxonomyOptions = {
  skipStoredCountry?: boolean;
  skipStoredPlatform?: boolean;
  includeAudiences?: boolean;
  labels?: string;
};

const mergeCategories = (categories: AudienceCategoryItem[]) => {
  const map = new Map<string, AudienceCategoryItem>();
  categories.forEach((category) => {
    const existing = map.get(category.code);
    if (!existing) {
      map.set(category.code, {
        ...category,
        audiences: [...category.audiences],
      });
      return;
    }

    const mergedAudiences = [...existing.audiences];
    category.audiences.forEach((aud) => {
      if (!mergedAudiences.find((existingAudience) => existingAudience.code === aud.code)) {
        mergedAudiences.push(aud);
      }
    });

    map.set(category.code, {
      ...existing,
      audiences: mergedAudiences,
    });
  });

  const compareByCode = (a: { code: string }, b: { code: string }) =>
    a.code.localeCompare(b.code, undefined, { numeric: true, sensitivity: 'base' });
  const compareByCategoryName = (a: AudienceCategoryItem, b: AudienceCategoryItem) =>
    a.display_name.localeCompare(b.display_name);

  return Array.from(map.values())
    .sort(compareByCategoryName)
    .map((category) => ({
      ...category,
      audiences: [...category.audiences].sort(compareByCode),
    }));
};

export const useTaxonomy = (options: UseTaxonomyOptions = {}) => {
  const {
    skipStoredCountry = false,
    skipStoredPlatform = false,
    includeAudiences = false,
    labels,
  } = options;
  const [countries, setCountries] = useState<CountryItem[]>([]);
  const [platforms, setPlatforms] = useState<AudiencePlatformItem[]>([]);
  const [audienceSets, setAudienceSets] = useState<AudienceSetItem[]>([]);
  const [audienceCategories, setAudienceCategories] = useState<AudienceCategoryItem[]>([]);
  const [isLoadingAudiences, setIsLoadingAudiences] = useState(false);

  const [selectedCountry, setSelectedCountry] = useState<CountryItem | undefined>(() => undefined);
  const [selectedPlatform, setSelectedPlatform] = useState<AudiencePlatformItem | undefined>(() => undefined);
  const [savedCountryCode] = useState<string | null>(() => (
    isBrowser ? getStoredCountryCode() : null
  ));
  const [savedPlatformCode] = useState<string | null>(() => (
    isBrowser ? getStoredPlatformCode() : null
  ));

  useEffect(() => {
    const api = new TaxonomyApi();

    api.listCountries()
      .then((resp) => {
        const rawCountries = resp.data.data || [];
        rawCountries.sort((a, b) => a.display_name.localeCompare(b.display_name));
        setCountries(rawCountries);

        if (savedCountryCode && !skipStoredCountry) {
          const match = rawCountries.find((c) => c.code === savedCountryCode);
          if (match) {
            setSelectedCountry(match);
          }
        }
      })
      .catch((err) => {
        console.error('Failed to load countries', err);
      });

    api.listAudiencePlatforms()
      .then((resp) => {
        const apiPlatforms = resp.data.data || [];
        apiPlatforms.sort((a, b) => a.display_name.localeCompare(b.display_name));
        setPlatforms(apiPlatforms);

        if (savedPlatformCode && !skipStoredPlatform) {
          const match = apiPlatforms.find((p) => p.code === savedPlatformCode);
          if (match) {
            setSelectedPlatform(match);
          }
        }
      })
      .catch((err) => {
        console.error('Failed to load platforms', err);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!includeAudiences) return;
    let cancelled = false;
    const api = new TaxonomyApi();
    const platformCode = selectedPlatform?.code ?? '';
    const countryCode = selectedCountry?.code ?? '';

    setIsLoadingAudiences(true);
    setAudienceSets([]);
    setAudienceCategories([]);

    api.listAudiences(platformCode, countryCode, undefined, undefined, undefined, undefined, labels)
      .then((resp) => {
        if (cancelled) return;
        const data = resp.data.data || [];
        let categories: AudienceCategoryItem[] = [];
        let sets: AudienceSetItem[] = [];
        if (data.length && Object.prototype.hasOwnProperty.call(data[0], 'categories')) {
          sets = data as AudienceSetItem[];
          categories = sets.flatMap((setItem) => setItem.categories);
        } else {
          categories = data as AudienceCategoryItem[];
        }

        setAudienceSets(sets);
        setAudienceCategories(mergeCategories(categories));
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load audiences', err);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingAudiences(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [includeAudiences, labels, selectedCountry?.code, selectedPlatform?.code]);

  useEffect(() => {
    if (!isBrowser || skipStoredCountry) return;
    if (selectedCountry) {
      setStoredCountryCode(selectedCountry.code);
    } else {
      setStoredCountryCode(null);
    }
  }, [selectedCountry, skipStoredCountry]);

  useEffect(() => {
    if (!isBrowser || skipStoredPlatform) return;
    if (selectedPlatform) {
      setStoredPlatformCode(selectedPlatform.code);
    } else {
      setStoredPlatformCode(null);
    }
  }, [selectedPlatform, skipStoredPlatform]);

  return {
    countries,
    platforms,
    audienceSets,
    audienceCategories,
    isLoadingAudiences,
    selectedCountry,
    setSelectedCountry,
    selectedPlatform,
    setSelectedPlatform,
  };
};
